import * as React from 'react';
import propz from 'propz';
import { Form, Formik } from 'formik';
import { REPORT_TYPE_SERVER_TO_CLIENT_MAPPING } from '../../../../../../../consts/report';
import { getReportDefaultGendersForMultiSelect, getReportAgesForMultiSelect } from '../../../../../../../helpers/multiselect/multiselect';
import { School } from '../../../../../../../models/school';
import { SchoolForm } from '../../../../../../../models/form';
import { getStartDateForSchoolYear } from '../../../../../../../helpers/calendar/calendar';
import { PeriodField } from 'Src/components/ReportFields/ReportFields';
import { ReportActions, ReportCheckboxGrid, ReportDateRange, ReportDescription, ReportSection } from 'Src/views/GenericView/AdminView/TeamsAndEvents/Events/SchoolEventView/Reports/ReportFormUi';
import '../../Clubs.css';

const OPTIONS = {
  IS_CURRENT_WEEK: 'isCurrentWeek',
  IS_PAST_WEEK: 'isPastWeek',
  IS_CUSTOM_INTERVAL: 'isCustomInterval'
};

interface Props {
  onCloseClick: () => void;
  onSubmit: (data: any, reportType: string) => void;
  school: School;
  forms: SchoolForm[];
}

const startOfDay = (date: Date) => new Date(new Date(date).setHours(0, 0, 1, 0));
const endOfDay = (date: Date) => new Date(new Date(date).setHours(23, 59, 59, 999));

const weekRange = (offset: number) => {
  const today = new Date();
  const day = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - (day === 0 ? 6 : day - 1) + offset * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { dateFrom: startOfDay(monday), dateTo: endOfDay(sunday) };
};

// report 065
export function AverageClubAttendanceReportForm({ onCloseClick, onSubmit, school, forms }: Props) {
  const genders = getReportDefaultGendersForMultiSelect();
  const ages = getReportAgesForMultiSelect(school, forms);
  const currentWeek = weekRange(0);

  const initialValues = {
    options: OPTIONS.IS_CURRENT_WEEK,
    isCurrentWeek: false,
    isPastWeek: false,
    isCustomInterval: false,
    genders,
    ages,
    isSelectAllAges: true,
    dateFrom: currentWeek.dateFrom,
    dateTo: currentWeek.dateTo
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={values => {
        const valuesToTransfer: any = { ...values };
        propz.set(valuesToTransfer, [values.options], true);
        delete valuesToTransfer.options;

        const range = values.options === OPTIONS.IS_CURRENT_WEEK
          ? weekRange(0)
          : values.options === OPTIONS.IS_PAST_WEEK
            ? weekRange(-1)
            : { dateFrom: values.dateFrom, dateTo: values.dateTo };

        valuesToTransfer.dateFrom = range.dateFrom;
        valuesToTransfer.dateTo = range.dateTo;
        onSubmit(valuesToTransfer, REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.AVERAGE_CLUB_ATTENDANCE);
      }}
    >
      {({ values, setFieldValue }) => {
        const selectedGenders = values.genders.map((item, index) => item.isChecked ? String(index) : '').filter(Boolean);
        const ageOptions = values.ages.map(item => ({ value: String(item.age), label: item.name }));
        const selectedAges = values.ages.filter(item => item.isChecked).map(item => String(item.age));
        const isValid = selectedGenders.length > 0 && selectedAges.length > 0;

        return (
          <Form className="averageClubAttendanceForm">
            <div className="averageClubAttendanceForm__scroll">
          <ReportDescription>One row per club, showing participant numbers for each session and the average number attending across the selected period.</ReportDescription>

              <ReportSection number={1} title="Reporting period" hint="Choose a preset period or enter a custom date range." required>
                <PeriodField
                  value={values.options}
                  onChange={option => {
                    setFieldValue('options', option);
                    if (option === OPTIONS.IS_CURRENT_WEEK || option === OPTIONS.IS_PAST_WEEK) {
                      const range = weekRange(option === OPTIONS.IS_CURRENT_WEEK ? 0 : -1);
                      setFieldValue('dateFrom', range.dateFrom);
                      setFieldValue('dateTo', range.dateTo);
                    } else {
                      const from = startOfDay(getStartDateForSchoolYear(new Date()));
                      setFieldValue('dateFrom', from);
                      setFieldValue('dateTo', endOfDay(new Date()));
                    }
                  }}
                  presets={[
                    { value: OPTIONS.IS_CURRENT_WEEK, label: 'This week' },
                    { value: OPTIONS.IS_PAST_WEEK, label: 'Last week' },
                    { value: OPTIONS.IS_CUSTOM_INTERVAL, label: 'Custom interval' }
                  ]}
                />
                {values.options === OPTIONS.IS_CUSTOM_INTERVAL && (
                  <div className="averageClubAttendanceForm__customRange">
                    <ReportDateRange
                      dateFrom={values.dateFrom}
                      dateTo={values.dateTo}
                      onChangeFrom={date => {
                        setFieldValue('dateFrom', date);
                        if (values.dateTo < date) setFieldValue('dateTo', date);
                      }}
                      onChangeTo={date => setFieldValue('dateTo', date)}
                    />
                  </div>
                )}
              </ReportSection>

              <ReportSection number={2} title="Gender" hint="Select at least one gender category.">
                <ReportCheckboxGrid items={values.genders} onChange={index => setFieldValue('genders', values.genders.map((item, itemIndex) => itemIndex === index ? { ...item, isChecked: !item.isChecked } : item))} />
              </ReportSection>

              <ReportSection
                number={3}
                title="Age group"
                hint="Select the age groups included in the report."
                action={<button type="button" onClick={() => {
                  const shouldSelectAll = selectedAges.length !== ageOptions.length;
                  setFieldValue('ages', values.ages.map(item => ({ ...item, isChecked: shouldSelectAll })));
                  setFieldValue('isSelectAllAges', shouldSelectAll);
                }}>{selectedAges.length === ageOptions.length ? 'Clear all' : 'Select all'}</button>}
              >
                <ReportCheckboxGrid items={values.ages} onChange={index => { const updatedAges = values.ages.map((item, itemIndex) => itemIndex === index ? { ...item, isChecked: !item.isChecked } : item); setFieldValue('ages', updatedAges); setFieldValue('isSelectAllAges', updatedAges.every(item => item.isChecked)); }} />
              </ReportSection>
            </div>
            <ReportActions onCancel={onCloseClick} disabled={!isValid} />
          </Form>
        );
      }}
    </Formik>
  );
}
